import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { NotificationList } from '@mnc/contracts';

import { api, qs } from '../lib/api';
import { pick } from '../i18n';
import { referenceRoute } from '../lib/references';
import { EmptyState, PageHeader, TableSkeleton, cn } from '../components/ui';

export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const list = useQuery({
    queryKey: ['notifications', { unreadOnly, page }],
    queryFn: () => api.get<NotificationList>(`/api/notifications${qs({ page, page_size: 25, unread_only: unreadOnly })}`),
  });
  const markAll = useMutation({
    mutationFn: () => api.post('/api/notifications/read', { all: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markOne = useMutation({
    mutationFn: (id: string) => api.post('/api/notifications/read', { ids: [id] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div>
      <PageHeader
        title={t('notifications.title')}
        subtitle={list.data ? t('notifications.unread', { count: list.data.unread_count }) : undefined}
        actions={
          <>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={unreadOnly} onChange={(e) => (setUnreadOnly(e.target.checked), setPage(1))} />
              {t('notifications.unreadOnly')}
            </label>
            <button type="button" className="btn-secondary" onClick={() => markAll.mutate()} disabled={!list.data?.unread_count}>
              {t('notifications.markAllRead')}
            </button>
          </>
        }
      />
      {list.isLoading && <TableSkeleton />}
      {list.data && list.data.items.length === 0 && <EmptyState>{t('notifications.empty')}</EmptyState>}
      {list.data && list.data.items.length > 0 && (
        <ul className="card divide-y divide-neutral-100 dark:divide-neutral-800">
          {list.data.items.map((n) => (
            <li key={n.id} className={cn('flex items-start gap-3 px-4 py-3', !n.is_read && 'bg-brand-50/50 dark:bg-brand-700/10')}>
              <span className={cn('mt-2 h-2 w-2 shrink-0 rounded-full', n.is_read ? 'bg-transparent' : 'bg-brand-600')} />
              <button
                type="button"
                className="flex-1 text-start"
                onClick={() => {
                  if (!n.is_read) markOne.mutate(n.id);
                  const to = referenceRoute(n.reference_type, n.reference_id);
                  if (to) void navigate({ to: to as never });
                }}
              >
                <div className="text-sm font-medium" dir="auto">
                  {pick(i18n.language, n.title_en, n.title_ar)}
                </div>
                {(n.body_en || n.body_ar) && (
                  <div className="text-sm text-neutral-600 dark:text-neutral-400" dir="auto">
                    {pick(i18n.language, n.body_en, n.body_ar)}
                  </div>
                )}
                <div className="mt-1 text-xs text-neutral-400">{new Date(n.created_at).toLocaleString(i18n.language)}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
